import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { RaceContestantPoints } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import { InputText, ValidatedText, InputSelect, DataSourceLookup, InputFloat, ValidatedFloat } from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class RaceContestantPointsGuiDef extends GuiDef<RaceContestantPoints> {
    constructor() {
        super('RaceContestantPoints');

        const EventCommon = storeToRefs(useEventCommonStore());
        const StationRolesSource = computed(() => {
            return EventCommon.StationRolesAsSelectLine.value;
        });

        const Comment = new FieldDef<RaceContestantPoints>();
        Comment.Name = 'Comment';
        Comment.FieldName = 'Comment';
        Comment.DefinedFor = 'Both';
        Comment.TranslationKey = 'racecontestantpoints.comment';
        Comment.Editable = true;
        Comment.Filterable = false;
        Comment.Sortable = true;
        Comment.CssClasses = '';
        Comment.value = (racecontestantpoints) => racecontestantpoints.Comment;
        Comment.setValue = (racecontestantpoints, value) => (racecontestantpoints.Comment = value);
        Comment.DetailEditor = markRaw(InputText);
        Comment.DetailEditorProps = {};
        Comment.CellEditor = markRaw(InputText);
        Comment.CellEditorProps = {};
        Comment.CellRenderer = markRaw(ValidatedText);
        Comment.CellRendererProps = {};
        Comment.Validator = null;
        Comment.SortDirection = undefined;
        Comment.cellClassRules = undefined;
        Comment.MinWidth = undefined;
        Comment.MaxWidth = undefined;
        Comment.Tooltip = null;
        this.Fields.push(Comment);

        const StationRoleID = new FieldDef<RaceContestantPoints>();
        StationRoleID.Name = 'StationRoleID';
        StationRoleID.FieldName = 'StationRoleID';
        StationRoleID.DefinedFor = 'Both';
        StationRoleID.TranslationKey = 'racecontestantpoints.stationroleid';
        StationRoleID.Editable = true;
        StationRoleID.Filterable = true;
        StationRoleID.Sortable = true;
        StationRoleID.CssClasses = '';
        StationRoleID.value = (racecontestantpoints) => racecontestantpoints.StationRoleID;
        StationRoleID.setValue = (racecontestantpoints, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantpoints.StationRoleID = value;
        };
        StationRoleID.DetailEditor = markRaw(InputSelect);
        StationRoleID.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: StationRolesSource } };
        StationRoleID.CellEditor = markRaw(InputSelect);
        StationRoleID.CellEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: StationRolesSource }, autoOpen: true };
        // @ts-expect-error @ts-ignore
        StationRoleID.CellRenderer = markRaw(DataSourceLookup);
        StationRoleID.CellRendererProps = { datasource: { mapsource: StationRolesSource } };
        StationRoleID.ExportValueFormatter = (obj: RaceContestantPoints) => {
            return StationRolesSource.value.get(obj.StationRoleID)?.text;
        };
        StationRoleID.Validator = null;
        StationRoleID.SortDirection = undefined;
        StationRoleID.cellClassRules = undefined;
        StationRoleID.MinWidth = undefined;
        StationRoleID.MaxWidth = undefined;
        StationRoleID.Tooltip = null;
        this.Fields.push(StationRoleID);

        const Points = new FieldDef<RaceContestantPoints>();
        Points.Name = 'Points';
        Points.FieldName = 'Points';
        Points.DefinedFor = 'Both';
        Points.TranslationKey = 'racecontestantpoints.points';
        Points.Editable = true;
        Points.Filterable = false;
        Points.Sortable = true;
        Points.CssClasses = '';
        Points.value = (racecontestantpoints) => racecontestantpoints.Points;
        Points.setValue = (racecontestantpoints, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            racecontestantpoints.Points = value;
        };
        Points.DetailEditor = markRaw(InputFloat);
        Points.DetailEditorProps = {};
        Points.CellEditor = markRaw(InputFloat);
        Points.CellEditorProps = {};
        Points.CellRenderer = markRaw(ValidatedFloat);
        Points.CellRendererProps = {};
        Points.Validator = null;
        Points.SortDirection = undefined;
        Points.cellClassRules = undefined;
        Points.MinWidth = 140;
        Points.MaxWidth = 140;
        Points.Tooltip = null;
        this.Fields.push(Points);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['StationRoles']);
    }
}
